import { useEffect, useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Html5Qrcode } from "html5-qrcode";
import { Button } from "@/components/ui/button";
import { Camera, ScanLine, ToggleLeft, CheckCircle2, AlertTriangle, RefreshCcw, ImagePlus } from "lucide-react";
import { toast } from "sonner";
import { markAttendanceSmart, getQueue, syncQueue, isOnline } from "@/lib/offline";

const todayISO = () => new Date().toISOString().slice(0, 10);

const MobileScan = () => {
  const scannerRef = useRef(null);
  const qrId = "mobile-qr-region";
  const [running, setRunning] = useState(false);
  const [cameraId, setCameraId] = useState(null);
  const [cameras, setCameras] = useState([]);
  const [perm, setPerm] = useState("unknown"); // unknown, granted, denied, prompt
  const [status, setStatus] = useState("present");
  const [date] = useState(todayISO());
  const [recent, setRecent] = useState([]);
  const [students, setStudents] = useState([]);
  const [photoMode, setPhotoMode] = useState(false);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const [manualPick, setManualPick] = useState("");
  const [queueCount, setQueueCount] = useState(getQueue().length);
  const [lastScanAt, setLastScanAt] = useState(0);

  // Load students list once
  useEffect(() => {
    api.get("/students").then((r) => setStudents(r.data)).catch(() => {});
  }, []);

  // Discover cameras + permission state
  useEffect(() => {
    (async () => {
      try {
        // try permissions API first
        if (navigator.permissions?.query) {
          try {
            const p = await navigator.permissions.query({ name: "camera" });
            setPerm(p.state);
            p.onchange = () => setPerm(p.state);
          } catch (e) { /* not supported */ }
        }
        const list = await Html5Qrcode.getCameras();
        setCameras(list || []);
        if (list && list.length > 0) {
          // prefer rear camera (last cam, or one with 'back' label)
          const back = list.find((c) => /back|rear|environment/i.test(c.label));
          setCameraId((back || list[list.length - 1]).id);
        }
      } catch (e) {
        setPerm("denied");
      }
    })();
  }, []);

  // Auto-request camera permission helper
  const requestCameraPermission = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: { ideal: "environment" } } });
      stream.getTracks().forEach((t) => t.stop());
      setPerm("granted");
      const list = await Html5Qrcode.getCameras();
      setCameras(list || []);
      if (list && list.length) {
        const back = list.find((c) => /back|rear|environment/i.test(c.label));
        setCameraId((back || list[list.length - 1]).id);
      }
      toast.success("Camera ready");
    } catch (e) {
      setPerm("denied");
      toast.error("Camera permission denied. Enable it in your browser settings.");
    }
  };

  const start = async () => {
    if (!cameraId) { await requestCameraPermission(); return; }
    try {
      const inst = new Html5Qrcode(qrId, { verbose: false });
      scannerRef.current = inst;
      await inst.start(
        cameraId,
        { fps: 15, qrbox: { width: 260, height: 260 }, aspectRatio: 1.0, disableFlip: false },
        async (decoded) => { await onDecoded(decoded); },
        () => {}
      );
      setRunning(true);
    } catch (e) {
      toast.error("Could not start scanner: " + (e?.message || e));
    }
  };

  const stop = async () => {
    try { await scannerRef.current?.stop(); await scannerRef.current?.clear(); } catch (e) {}
    scannerRef.current = null;
    setRunning(false);
  };

  const switchCamera = async () => {
    if (cameras.length < 2) return;
    const idx = cameras.findIndex((c) => c.id === cameraId);
    const next = cameras[(idx + 1) % cameras.length];
    setCameraId(next.id);
    if (running) { await stop(); setTimeout(start, 200); }
  };

  const onDecoded = async (text) => {
    // throttle 1.5s
    const now = Date.now();
    if (now - lastScanAt < 1500) return;
    setLastScanAt(now);

    let resolved;
    try { resolved = (await api.post("/id-cards/scan/resolve", { value: text })).data; }
    catch (e) { navigator.vibrate?.(60); toast.error(formatApiError(e)); return; }
    if (resolved.person_type !== "student") return toast.error("This is not a student attendance card");
    const stu = students.find((s) => s.id === resolved.person_id);
    navigator.vibrate?.([20, 30, 20]);
    const r = await markAttendanceSmart({
      subject_id: resolved.person_id, subject_type: "student", date, status, method: "qr",
    });
    setQueueCount(getQueue().length);
    setRecent((p) => [
      { name: stu.full_name, adm: stu.admission_no, status, mode: r.mode, at: new Date().toLocaleTimeString() },
      ...p,
    ].slice(0, 30));
  };

  // Photo capture
  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: "environment" }, width: { ideal: 1280 } },
        audio: false,
      });
      if (videoRef.current) videoRef.current.srcObject = stream;
      setPhotoMode(true);
    } catch (e) { toast.error("Camera permission required."); }
  };
  const stopCamera = () => {
    const s = videoRef.current?.srcObject;
    if (s) s.getTracks().forEach((t) => t.stop());
    setPhotoMode(false);
  };
  const capture = async () => {
    if (!manualPick) return toast.error("Pick a student first");
    if (!videoRef.current) return;
    const c = canvasRef.current;
    c.width = videoRef.current.videoWidth; c.height = videoRef.current.videoHeight;
    c.getContext("2d").drawImage(videoRef.current, 0, 0);
    const photo = c.toDataURL("image/jpeg", 0.5);
    const stu = students.find((s) => s.id === manualPick);
    const r = await markAttendanceSmart({
      subject_id: manualPick, subject_type: "student", date, status, method: "photo", photo_data: photo,
    });
    setQueueCount(getQueue().length);
    setRecent((p) => [{ name: stu?.full_name, adm: stu?.admission_no, status, mode: r.mode, at: new Date().toLocaleTimeString() }, ...p].slice(0, 30));
    navigator.vibrate?.(30);
    toast.success(`Marked ${stu?.full_name} (${r.mode})`);
  };

  const doSync = async () => {
    const r = await syncQueue();
    setQueueCount(getQueue().length);
    toast.success(`Synced ${r.synced} · ${r.remaining} pending`);
  };

  return (
    <div className="px-4 pt-4 pb-6" data-testid="mobile-scan-page">
      {/* Status + sync */}
      <div className="flex items-center gap-2 mb-3">
        <button
          onClick={() => setStatus("present")}
          className={"flex-1 py-2.5 rounded-full text-sm font-medium transition " + (status === "present" ? "bg-emerald-600 text-white" : "bg-white text-[#1B2C5A] border border-[#E2E8F0]")}
          data-testid="status-present"
        >Present</button>
        <button
          onClick={() => setStatus("late")}
          className={"flex-1 py-2.5 rounded-full text-sm font-medium transition " + (status === "late" ? "bg-amber-500 text-white" : "bg-white text-[#1B2C5A] border border-[#E2E8F0]")}
          data-testid="status-late"
        >Late</button>
        <button
          onClick={() => setStatus("absent")}
          className={"flex-1 py-2.5 rounded-full text-sm font-medium transition " + (status === "absent" ? "bg-red-600 text-white" : "bg-white text-[#1B2C5A] border border-[#E2E8F0]")}
          data-testid="status-absent"
        >Absent</button>
      </div>

      {/* Scanner viewport */}
      <div className="relative rounded-2xl overflow-hidden bg-black border border-[#E2E8F0] aspect-square">
        {!running && !photoMode && (
          <div className="absolute inset-0 grid place-items-center text-center text-white p-6">
            <div>
              <div className="w-16 h-16 mx-auto rounded-full bg-[#9DCD3F]/20 grid place-items-center text-[#9DCD3F] mb-4">
                <ScanLine className="w-8 h-8" />
              </div>
              <div className="font-display text-lg">{perm === "denied" ? "Camera blocked" : "Ready to scan"}</div>
              <div className="text-xs text-white/70 mt-1 px-4">
                {perm === "denied"
                  ? "Allow camera access in your browser settings, then tap Retry."
                  : "Tap Start to use the rear camera and scan student ID QR codes."}
              </div>
              <div className="mt-5 flex justify-center gap-2">
                {perm === "denied" ? (
                  <Button onClick={requestCameraPermission} className="bg-[#9DCD3F] text-[#1B2C5A] hover:bg-[#88BB35] rounded-full" data-testid="retry-perm">
                    <RefreshCcw className="w-4 h-4 mr-1.5" /> Retry Camera
                  </Button>
                ) : (
                  <Button onClick={start} className="bg-[#9DCD3F] text-[#1B2C5A] hover:bg-[#88BB35] rounded-full px-6" data-testid="start-scan">
                    <ScanLine className="w-4 h-4 mr-1.5" /> Start Scanner
                  </Button>
                )}
              </div>
            </div>
          </div>
        )}
        <div id={qrId} className="w-full h-full" />
        {photoMode && (
          <>
            <video ref={videoRef} autoPlay playsInline muted className="absolute inset-0 w-full h-full object-cover" />
            <canvas ref={canvasRef} className="hidden" />
          </>
        )}
        {/* Aim corners overlay */}
        {running && (
          <div className="absolute inset-0 pointer-events-none">
            <div className="absolute inset-8 border-2 border-[#9DCD3F]/80 rounded-2xl" />
          </div>
        )}
      </div>

      {/* Controls */}
      <div className="mt-3 grid grid-cols-2 gap-2">
        {running ? (
          <Button onClick={stop} variant="outline" className="rounded-full h-12 border-[#E2E8F0]" data-testid="stop-scan">Stop</Button>
        ) : (
          <Button onClick={start} disabled={perm === "denied"} className="rounded-full h-12 bg-[#1B2C5A] text-white" data-testid="start-scan-btn">
            <ScanLine className="w-4 h-4 mr-1.5" /> {photoMode ? "QR Scanner" : "Start Scanner"}
          </Button>
        )}
        {photoMode ? (
          <Button onClick={stopCamera} variant="outline" className="rounded-full h-12 border-[#E2E8F0]">Close Camera</Button>
        ) : (
          <Button onClick={startCamera} variant="outline" className="rounded-full h-12 border-[#E2E8F0]" data-testid="start-photo">
            <ImagePlus className="w-4 h-4 mr-1.5" /> Photo Mode
          </Button>
        )}
        {cameras.length > 1 && (
          <Button onClick={switchCamera} variant="outline" className="rounded-full h-10 col-span-2 border-[#E2E8F0]" data-testid="switch-cam">
            <ToggleLeft className="w-4 h-4 mr-1.5" /> Switch camera ({cameras.length})
          </Button>
        )}
      </div>

      {/* Photo mode picker */}
      {photoMode && (
        <div className="mt-3 p-3 rounded-xl bg-white border border-[#E2E8F0]">
          <select
            value={manualPick}
            onChange={(e) => setManualPick(e.target.value)}
            className="w-full h-11 px-3 rounded-md border border-[#E2E8F0] text-sm bg-white"
            data-testid="photo-pick-student"
          >
            <option value="">Select student to mark…</option>
            {students.map((s) => <option key={s.id} value={s.id}>{s.full_name} — {s.admission_no}</option>)}
          </select>
          <Button onClick={capture} disabled={!manualPick} className="w-full mt-2 h-12 rounded-full bg-[#9DCD3F] text-[#1B2C5A] hover:bg-[#88BB35]" data-testid="photo-capture">
            <Camera className="w-4 h-4 mr-1.5" /> Capture & Mark {status}
          </Button>
        </div>
      )}

      {/* Offline queue indicator */}
      {queueCount > 0 && (
        <div className="mt-3 flex items-center gap-3 p-3 rounded-xl bg-amber-50 border border-amber-200" data-testid="queue-banner">
          <AlertTriangle className="w-4 h-4 text-amber-700 shrink-0" />
          <div className="text-xs text-amber-900 flex-1">
            <strong>{queueCount}</strong> attendance record{queueCount > 1 ? "s" : ""} waiting to sync.
            {!isOnline() && " You're offline — they will sync automatically when reconnected."}
          </div>
          {isOnline() && <Button size="sm" onClick={doSync} className="bg-[#1B2C5A] text-white">Sync now</Button>}
        </div>
      )}

      {/* Recent */}
      <div className="mt-5">
        <div className="overline text-[#9DCD3F] mb-2">Just now</div>
        {recent.length === 0 ? (
          <div className="text-sm text-[#475569] text-center py-6 border border-dashed border-[#E2E8F0] rounded-xl">No scans yet today.</div>
        ) : (
          <ul className="space-y-2" data-testid="recent-scans">
            {recent.map((r, i) => (
              <li key={i} className="flex items-center gap-3 p-3 rounded-xl bg-white border border-[#E2E8F0]">
                <div className={"w-9 h-9 rounded-full grid place-items-center " + (r.status === "absent" ? "bg-red-50 text-red-700" : r.status === "late" ? "bg-amber-50 text-amber-700" : "bg-emerald-50 text-emerald-700")}>
                  <CheckCircle2 className="w-4 h-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-[#1B2C5A] truncate">{r.name}</div>
                  <div className="text-xs text-[#475569]">{r.adm} · {r.status} · {r.at}</div>
                </div>
                <span className={"text-[10px] uppercase tracking-widest " + (r.mode === "queued" ? "text-amber-700" : "text-[#9DCD3F]")}>{r.mode}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

export default MobileScan;
